import type { ISODateString } from '@/shared/types';
import type {
  AccountInfoDto,
  AccountLeverageDto,
  AccountTypeName,
} from '../api';

export const ACCOUNTS_SECTION_TYPES = {
  ewallet: 'eWallet',
  invest: 'invest',
  spot: 'spot',
  leverage: 'leverage',
  earn: 'earn',
} as const;

export type AccountType = keyof typeof ACCOUNTS_SECTION_TYPES;

// TODO: move to shared
export const ASSETS_GROUPS = {
  crypto: 'Crypto',
  fiat: 'Fiat',
  unknown: 'Unknown',
} as const;

export type AssetGroup = (typeof ASSETS_GROUPS)[keyof typeof ASSETS_GROUPS];

export type RegularRests = {
  baseAvailable: number;
  baseReserved: number;
  baseAvailable00h: number;
  baseReserved00h: number;
};

export type AccountsByType = {
  ewallet: Array<AccountInfoDto>;
  invest: Array<AccountInfoDto>;
  spot: Array<AccountInfoDto>;
  leverage: Array<AccountInfoDto>;
  earn: Array<AccountInfoDto>;
};

export type AccountsRestsByType = {
  ewallet: Array<AccountsRestsBaseResponseDto>;
  invest: AccountsMarginRestsBaseResponseDto | null;
  spot: Array<AccountsRestsBaseResponseDto>;
  leverage: AccountsMarginRestsBaseResponseDto | null;
  earn: AccountsMarginRestsBaseResponseDto | null;
};

export type BalanceAsset = {
  code: string;
  name: string;
  precision: number;
  group: AssetGroup;
};

export type MarginAssetRests = BalanceAsset & {
  equity: number | undefined;
  baseEquity: number | undefined;
  baseEquity00h: number | undefined;
  margin: number | undefined;
  baseMargin: number | undefined;
};

export type AssetRests = BalanceAsset & {
  available: number;
  reserved: number;
  baseAvailable?: number;
  baseReserved?: number;
  baseAvailable00h?: number;
  baseReserved00h?: number;
};

export type AccountRest = {
  accTypeName: AccountTypeName;
  accNum: string;
  accName?: string;
  tradingAccKey?: number;
  tradingProductCode?: string;
  assets: AssetRests[];
  balance?: number;
};

export type AccountsRestsBaseResponseDto = {
  baseCode: string;
  baseName: string;
  basePrecission: number;
  baseLowBalance: number;
  accounts: AccountRest[];
};

export type AccountMarginRest = {
  accTypeName: AccountTypeName;
  accNum: string;
  accName?: string;
  tradingAccKey: number;
  tradingProductCode: string;
  balance?: number;
  balanceAsset?: BalanceAsset;
  baseBalance?: number;
  baseBalance00h?: number;
  profit?: number;
  baseProfit?: number;
  swap?: number;
  baseSwap?: number;
  rebate?: number;
  baseRebate?: number;
  equity?: number;
  baseEquity?: number;
  baseEquity00h?: number;
  margin?: number;
  baseMargin?: number;
  marginLevel?: number;
  commission?: number;
  baseComission?: number;
};

export type AccountsMarginRestsBaseResponseDto = {
  baseCode: string;
  baseName: string;
  basePrecission: number;
  baseLowBalance: number;
  accounts: AccountMarginRest[];
};

export type BalanceData = {
  balance: number;
  deltaPercent: number | null;
};

export type MarginAccount = {
  accNum: string;
  assets: Array<MarginAssetRests>;
  tradingAccKey?: number;
  tradingProductCode?: string;
  leverage?: AccountLeverageDto;
};

export type MarginIndicators = {
  equity: number;
  baseEquity: number;
  baseEquity00h: number;
  margin: number;
  baseMargin: number;
};

export type RegularAccount = {
  accNum: string;
  accTypeName: AccountTypeName;
  assets: Array<AssetRests>;
  tradingAccKey?: number;
  tradingProductCode?: string;
  leverage?: AccountLeverageDto;
  marginIndicators?: MarginIndicators;
};

export type AccontTypeData<T extends RegularAccount | MarginAccount> =
  BalanceData & {
    accounts: Array<T>;
    tradingAccKey?: number;
    created: ISODateString;
  };

export type ActionButtonsVisibility = {
  deposit: boolean;
  withdraw: boolean;
  exchange: boolean;
  transfer: boolean;
  trade: boolean;
};

export type GetAssetsActionButtonsVisibility = (
  accountType: keyof AccountsRestsByType,
  assetCode: string,
  assetBalance: number
) => ActionButtonsVisibility;
